import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api } from "../api";
import { CUSTOMER_REPORTED, STATUS_LABELS, caseKey, putProgram, type CaseProgram, type Followup } from "../caseApi";
import { useProgramPanel } from "../programPanel";

/** Programs recommended for the case, each with a status select, and the case's follow-ups. */
export function CaseProgramsPanel({ caseId, state, programs, followups, readOnly = false }: {
  caseId: string; state: string | null; programs: CaseProgram[]; followups: Followup[]; readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const panel = useProgramPanel();
  // Follow-up lists on Home and the Follow-ups page are cached too: refresh them with the case.
  const refresh = () => { qc.invalidateQueries({ queryKey: caseKey(caseId) }); qc.invalidateQueries({ queryKey: ["followups"] }); };
  const set = useMutation({
    mutationFn: (v: { p: CaseProgram; status: CaseProgram["status"] }) => putProgram(caseId, v.p, v.p.programId, v.status, state),
    onSuccess: refresh,
  });
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const add = useMutation({
    mutationFn: () => api(`/api/cases/${caseId}/followups`, {
      method: "POST", body: { dueAt: new Date(`${date}T09:00:00`).toISOString(), ...(note.trim() ? { note: note.trim() } : {}) },
    }),
    onSuccess: () => { setDate(""); setNote(""); refresh(); },
  });
  const done = useMutation({
    mutationFn: (id: string) => api(`/api/followups/${id}`, { method: "PATCH", body: { doneAt: new Date().toISOString() } }),
    onSuccess: refresh,
  });
  const submit = (e: FormEvent) => { e.preventDefault(); if (date) add.mutate(); };
  const open = followups.filter((f) => !f.doneAt);

  return (
    <section className="card" aria-label="Programs and follow-ups">
      <h2 className="mb-2 text-sm font-semibold">Programs for this case</h2>
      {programs.length === 0 && <p className="text-sm text-slate-500">None yet. Use “Add to case” on a match result.</p>}
      <ul className="space-y-2">
        {programs.map((p) => {
          const name = p.name ?? p.programId;
          return (
            <li key={p.programId} className="flex flex-wrap items-center gap-2 text-sm">
              {p.slug ? <button type="button" className="font-medium text-brand-700 hover:underline dark:text-brand-100"
                onClick={() => panel.open(p.slug!, p.stateCode ?? state ?? undefined)}>{name}</button> : <span className="font-medium">{name}</span>}
              <select aria-label={`Status of ${name}`} className="input w-36" value={p.status} disabled={readOnly || set.isPending}
                onChange={(e) => set.mutate({ p, status: e.target.value as CaseProgram["status"] })}>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {CUSTOMER_REPORTED.has(p.status) && <span className="text-xs text-slate-500">customer reported</span>}
              {!readOnly && p.status === "recommended" && (
                <button type="button" className="btn-ghost border border-slate-300" disabled={set.isPending}
                  onClick={() => set.mutate({ p, status: "referred" })}>Mark referred</button>
              )}
            </li>
          );
        })}
      </ul>
      {set.isError && <p role="alert" className="mt-2 text-sm text-red-600">Could not update the program.</p>}

      <h2 className="mb-2 mt-5 text-sm font-semibold">Follow-ups</h2>
      <ul className="space-y-1 text-sm">
        {open.map((f) => (
          <li key={f.id} className="flex items-center gap-2">
            <span>{new Date(f.dueAt).toLocaleDateString()}</span><span className="text-slate-600 dark:text-slate-400">{f.note}</span>
            {!readOnly && <button type="button" className="btn-ghost" disabled={done.isPending} onClick={() => done.mutate(f.id)}>Done</button>}
          </li>
        ))}
        {open.length === 0 && <li className="text-slate-500">No open follow-ups.</li>}
      </ul>
      {!readOnly && (
        <form onSubmit={submit} className="mt-3 flex flex-wrap items-end gap-2 text-sm">
          <div><label className="label" htmlFor="fu-date">Follow-up date</label>
            <input id="fu-date" type="date" required className="input" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="min-w-40 flex-1"><label className="label" htmlFor="fu-note">Follow-up note</label>
            <input id="fu-note" className="input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <button type="submit" className="btn-primary" disabled={add.isPending}>Add follow-up</button>
        </form>
      )}
      {add.isError && <p role="alert" className="mt-2 text-sm text-red-600">Could not add the follow-up.</p>}
    </section>
  );
}
